import Button from 'components/Form/Button';
import { Icon } from 'components/Icon';
import { CoreContext } from 'context/CoreContext';
import { useContext, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { PostImage } from 'services/api';
import { BaixarLote, Importar } from 'services/cobranca';
import { Title } from 'ui/styled';
import { exposeStrapiError } from 'utils';
import Wrapper from '../Wrapper';
import {
  Acoes,
  AreaArquivo,
  AreaTexto,
  ArquivoLinha,
  ArquivoNome,
  ArquivoTamanho,
  Bloco,
  BlocoTitulo,
  Conteudo,
  Instrucoes,
  Item,
  Resumo,
  ResumoItem,
  ResumoLabel,
  ResumoValor,
} from './styled';

// O back escolhe o leitor pela extensao do arquivo salvo, nao pelo mime.
const EXTENSOES = ['.xlsx', '.xls', '.csv'];

// Carteira e baixa compartilham o fluxo (upload -> endpoint -> relatorio) e
// so divergem no endpoint, no texto e em como o relatorio nomeia os numeros.
const MODOS = {
  carteira: {
    titulo: 'Importar carteira de cobrança',
    enviar: Importar,
    rotuloAcao: 'Importar',
    rotuloEnviando: 'Importando...',
    // quantas linhas o back gravou, e sob que nome vem no relatorio
    refGravadas: 'importados',
    rotuloGravadas: 'Importados',
    refContratos: 'cobrancas_criadas',
    sucesso: (rel) => `${rel?.importados || 0} título(s) importado(s).`,
    contratoTexto: (item) =>
      `${item?.contrato} — ${item?.criada ? 'criado' : 'atualizado'} (id ${item?.id})`,
  },
  baixa: {
    titulo: 'Importar baixa de títulos',
    enviar: BaixarLote,
    rotuloAcao: 'Baixar',
    rotuloEnviando: 'Baixando...',
    refGravadas: 'baixados',
    rotuloGravadas: 'Baixados',
    refContratos: 'contratos',
    sucesso: (rel) => `${rel?.baixados || 0} parcela(s) baixada(s).`,
    contratoTexto: (item) =>
      `${item?.contrato} — parcela(s) ${(item?.parcelas || []).join(', ')}`,
  },
};

const tamanhoEmMB = (bytes) => (bytes / 1024 / 1024).toFixed(2);

/**
 * Importacao por planilha da cobranca: sobe o arquivo para a biblioteca de
 * midia e manda o id para o endpoint do modo escolhido.
 *
 * modo 'carteira' -> /cobrancas/importar   (cria os titulos)
 * modo 'baixa'    -> /cobrancas/baixarlote (baixa parcelas ja existentes)
 *
 * Os dois sao tudo ou nada — qualquer erro no arquivo aborta o lote inteiro
 * sem gravar nada — entao o relatorio de volta precisa aparecer por completo,
 * nao so um toast.
 */
export default function ImportarCobranca({ reload, modo = 'carteira' }) {
  const config = MODOS[modo] || MODOS.carteira;
  const { setModal } = useContext(CoreContext);
  const inputRef = useRef(null);
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [relatorio, setRelatorio] = useState(null);

  const fechar = () => setModal(null);

  const selecionar = (lista) => {
    const escolhido = lista?.[0];
    if (!escolhido) {
      return;
    }
    const nome = `${escolhido.name}`.toLowerCase();
    if (!EXTENSOES.some((ext) => nome.endsWith(ext))) {
      toast.error('Formato não suportado. Envie .xlsx, .xls ou .csv.');
      return;
    }
    setArquivo(escolhido);
    // relatorio antigo nao vale para o arquivo novo
    setRelatorio(null);
  };

  const importar = async () => {
    if (!arquivo) {
      toast.error('Selecione o arquivo da carteira.');
      return;
    }

    setEnviando(true);
    try {
      // PostImage manda o File como veio; UploadImage converteria para png e
      // o back precisa da extensao original para saber ler a planilha.
      const enviado = await PostImage(arquivo);
      if (exposeStrapiError(enviado)) {
        return;
      }
      const idArquivo = enviado?.[0]?.id;
      if (!idArquivo) {
        toast.error('Não foi possível enviar o arquivo.');
        return;
      }

      const result = await config.enviar({ data: { id_arquivo: idArquivo } });

      // Recusa: 400 com o relatorio completo em error.details. Nada foi gravado.
      if (result?.error) {
        exposeStrapiError(result);
        setRelatorio({ ...(result?.error?.details || {}), gravado: false });
        return;
      }

      setRelatorio(result);
      toast.success(config.sucesso(result));
      reload?.();
    } catch (err) {
      console.error(`Erro ao processar planilha (${modo}):`, err);
      toast.error('Erro ao processar o arquivo');
    } finally {
      setEnviando(false);
    }
  };

  const listaBloco = (titulo, cor, itens) =>
    !itens?.length ? null : (
      <Bloco>
        <BlocoTitulo color={cor}>
          {titulo} ({itens.length})
        </BlocoTitulo>
        {itens.map((item, indice) => (
          <Item key={indice}>{item}</Item>
        ))}
      </Bloco>
    );

  return (
    <Wrapper onClose={fechar} title={config.titulo}>
      <Conteudo>
        {relatorio ? null : (
          <>
            <Instrucoes>
              Arquivo <strong>.xlsx</strong>, <strong>.xls</strong> ou{' '}
              <strong>.csv</strong>. O sistema procura a primeira aba que tenha
              o layout esperado.
              <br />
              Colunas obrigatórias:{' '}
              {modo === 'baixa' ? (
                <>
                  <strong>cnpj_distribuidor</strong>, <strong>contrato</strong>,{' '}
                  <strong>parcela</strong>, <strong>data_baixa</strong> e{' '}
                  <strong>valor_recebido</strong> (total recebido: principal +
                  multa + mora). <strong>multa</strong> e <strong>mora</strong>{' '}
                  são opcionais.
                </>
              ) : (
                <>
                  <strong>contrato</strong>, <strong>parcela</strong>,{' '}
                  <strong>data_vencimento</strong>, <strong>valor</strong>,{' '}
                  <strong>cpf_cnpj_cliente</strong>,{' '}
                  <strong>nome_cliente</strong>,{' '}
                  <strong>cnpj_distribuidor</strong> e{' '}
                  <strong>cnpj_banco</strong>.
                </>
              )}
              <br />É tudo ou nada: qualquer erro no arquivo aborta o lote
              inteiro e nada é gravado. Reenviar o mesmo arquivo é seguro —{' '}
              {modo === 'baixa'
                ? 'parcela já baixada é pulada, nunca baixada duas vezes.'
                : 'título já existente é pulado, nunca duplicado.'}
              {modo !== 'baixa' ? null : (
                <>
                  <br />
                  Não existe baixa parcial: se o valor recebido for menor que o
                  da parcela, ela é baixada integralmente e você recebe um
                  aviso.
                </>
              )}
            </Instrucoes>

            <input
              ref={inputRef}
              type="file"
              style={{ display: 'none' }}
              accept={EXTENSOES.join(',')}
              disabled={enviando}
              onChange={(e) => selecionar(e.target.files)}
            />

            <AreaArquivo
              $arrastando={arrastando}
              onClick={() => !enviando && inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setArrastando(true);
              }}
              onDragLeave={() => setArrastando(false)}
              onDrop={(e) => {
                e.preventDefault();
                setArrastando(false);
                selecionar(e.dataTransfer.files);
              }}
            >
              <Icon name="upload_file" size={40} color="#2670D1" />
              <Title small nomargin centred>
                Arraste a planilha aqui
              </Title>
              <AreaTexto>ou clique para escolher no computador</AreaTexto>
            </AreaArquivo>

            {arquivo ? (
              <ArquivoLinha>
                <Icon name="description" size={20} color="#2670D1" />
                <ArquivoNome>{arquivo.name}</ArquivoNome>
                <ArquivoTamanho>{tamanhoEmMB(arquivo.size)} MB</ArquivoTamanho>
                <Icon
                  name="close"
                  size={18}
                  color="#a4a4a4"
                  onClick={() => !enviando && setArquivo(null)}
                  elementAttributes={{
                    title: 'Remover arquivo',
                    style: { cursor: 'pointer' },
                  }}
                />
              </ArquivoLinha>
            ) : null}
          </>
        )}

        {relatorio ? (
          <>
            <Resumo $gravado={!!relatorio?.gravado}>
              <ResumoItem>
                <ResumoLabel>Linhas</ResumoLabel>
                <ResumoValor>{relatorio?.total_linhas ?? '-'}</ResumoValor>
              </ResumoItem>
              <ResumoItem>
                <ResumoLabel>{config.rotuloGravadas}</ResumoLabel>
                <ResumoValor>
                  {relatorio?.[config.refGravadas] || 0}
                </ResumoValor>
              </ResumoItem>
              <ResumoItem>
                <ResumoLabel>Ignorados</ResumoLabel>
                <ResumoValor>{relatorio?.ignorados?.length || 0}</ResumoValor>
              </ResumoItem>
              <ResumoItem>
                <ResumoLabel>Contratos</ResumoLabel>
                <ResumoValor>
                  {relatorio?.[config.refContratos]?.length || 0}
                </ResumoValor>
              </ResumoItem>
              {/* O back procura a aba com o layout, nao assume a primeira:
                  saber qual foi lida resolve o "mandei a planilha certa". */}
              {!relatorio?.aba_lida ? null : (
                <ResumoItem>
                  <ResumoLabel>Aba lida</ResumoLabel>
                  <ResumoValor>{relatorio.aba_lida}</ResumoValor>
                </ResumoItem>
              )}
            </Resumo>

            {listaBloco(
              'Erros — nada foi gravado',
              '#FF3B30',
              relatorio?.erros
            )}
            {listaBloco('Avisos', '#FF9500', relatorio?.avisos)}
            {listaBloco(
              'Títulos ignorados',
              '#718096',
              (relatorio?.ignorados || []).map(
                (item) =>
                  `linha ${item?.linha}: ${item?.numero_titulo || item?.chave} — ${item?.motivo}`
              )
            )}
            {listaBloco(
              'Contratos',
              '#718096',
              (relatorio?.[config.refContratos] || []).map(config.contratoTexto)
            )}
            {listaBloco(
              'Colunas ignoradas do arquivo',
              '#718096',
              relatorio?.colunas_desconhecidas
            )}
          </>
        ) : null}

        <Acoes>
          {relatorio ? (
            <Button
              color="lightgrey"
              small
              nospace
              width="auto"
              onClick={() => {
                setRelatorio(null);
                setArquivo(null);
              }}
            >
              Enviar outro arquivo
            </Button>
          ) : (
            <Button
              color="blue"
              small
              nospace
              width="auto"
              loading={enviando}
              disabled={enviando || !arquivo}
              onClick={importar}
            >
              {enviando ? config.rotuloEnviando : config.rotuloAcao}
            </Button>
          )}
          <Button
            color="lightgrey"
            small
            nospace
            width="auto"
            disabled={enviando}
            onClick={fechar}
          >
            Fechar
          </Button>
        </Acoes>
      </Conteudo>
    </Wrapper>
  );
}
